/**
 * Structured bilingual blocks for Events guides (Mu Beginning).
 * Player-facing EN/HE — no internal Data / source filenames.
 */

import { guideImg } from "./guideAssets";

function fmtZen(n) {
  return Number(n).toLocaleString("en-US");
}

/** Shared notes for timed PvE events (BC / DS / CC / IT / Imperial / Kanturu). */
function timedPveSharedNotes() {
  return [
    {
      type: "note",
      en: "Live countdowns: open the website Events page (/events) or in-game Event Time. Times use Asia/Jerusalem / server time.",
      he: "ספירות לאחור חיות: פתחו את דף Events באתר (/events) או את Event Time במשחק. הזמנים לפי Asia/Jerusalem / שעון השרת.",
    },
    {
      type: "note",
      en: "These classic timed events are separate from the custom Dungeon System Relics. For daily Relic dungeon instances see Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
      he: "אירועים מתוזמנים קלאסיים אלה נפרדים ממערכת ה־Dungeon Relics המותאמת. למופעי מבוך יומיים עם Relic ראו Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
    },
    {
      type: "note",
      en: "Your entry ticket, cloak, invitation, or scroll is consumed when you enter. Bring a spare if you plan more than one run.",
      he: "כרטיס הכניסה, ה־Cloak, ההזמנה או המגילה נצרכים בכניסה. הביאו גיבוי אם אתם מתכננים יותר מריצה אחת.",
    },
  ];
}

const BC_CLOAK_ZEN = [
  [1, 50000],
  [2, 80000],
  [3, 150000],
  [4, 250000],
  [5, 400000],
  [6, 600000],
  [7, 850000],
  [8, 1050000],
];

const DS_INVITE_ZEN = [
  [1, 100000],
  [2, 200000],
  [3, 400000],
  [4, 700000],
  [5, 1100000],
  [6, 1600000],
  [7, 2000000],
];

const IT_SCROLL_ZEN = [
  [1, 3000000],
  [2, 5000000],
  [3, 7000000],
  [4, 9000000],
  [5, 11000000],
  [6, 13000000],
];

const guideEventsContent = {
  "blood-castle": [
    ...timedPveSharedNotes(),
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Blood Castle is a party PvE event. Clear monsters, destroy the bridge and gate, reach the Archangel statue, obtain the Absolute Weapon, and deliver it to Archangel King. Up to 10 players per tier.",
      he: "Blood Castle הוא אירוע PvE בפארטי. נקו מפלצות, השמידו את הגשר והשער, הגיעו לפסל Archangel, קחו את Absolute Weapon ומסרו אותו ל־Archangel King. עד 10 שחקנים בכל דרגה.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Daily starts (Asia/Jerusalem / server time): 00:00, 04:00, 08:00, 12:00, 16:00, 20:00.",
      he: "התחלות יומיות (Asia/Jerusalem / שעון השרת): 00:00, 04:00, 08:00, 12:00, 16:00, 20:00.",
    },
    {
      type: "p",
      en: "Phases: Warning 5 · Notify 1 · Event 15 · Close 4 minutes. Watch /events or Event Time for the live countdown.",
      he: "שלבים: Warning 5 · Notify 1 · Event 15 · Close 4 דקות. עקבו אחרי /events או Event Time לספירה לאחור חיה.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Devias — Messenger of Archangel / Angel Messenger near the Blood Castle entrance area (around 209, 30 and 219, 9).",
      he: "Devias — Messenger of Archangel / Angel Messenger ליד אזור הכניסה ל־Blood Castle (בסביבות 209, 30 ו־219, 9).",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Invisibility Cloak (13,18) with level = your Blood Castle tier + 1, OR Blood Castle Ticket (13,47). The cloak or ticket is consumed on entry. Speak to the NPC while holding a matching entry item during the open window.",
      he: "Invisibility Cloak (13,18) ברמה = דרגת Blood Castle שלכם + 1, או Blood Castle Ticket (13,47). ה־Cloak או הכרטיס נצרכים בכניסה. דברו עם ה־NPC כשיש לכם פריט כניסה תואם בחלון הפתוח.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Prepare the correct Invisibility Cloak +N or Blood Castle Ticket. 2) Go to Devias near the BC entrance NPCs. 3) When Event Time shows Blood Castle open, speak to Messenger of Archangel / Angel Messenger with the matching cloak or ticket.",
      he: "1) הכינו Invisibility Cloak +N נכון או Blood Castle Ticket. 2) הגיעו ל־Devias ליד ה־NPC של הכניסה. 3) כש־Event Time מציג Blood Castle פתוח, דברו עם Messenger of Archangel / Angel Messenger עם ה־Cloak או הכרטיס התואם.",
    },
    {
      type: "h",
      en: "Ticket / Chaos Machine",
      he: "כרטיס / Chaos Machine",
    },
    {
      type: "p",
      en: "Chaos Machine recipe: Jewel of Chaos ×1 + Scroll of Archangel +N + Blood Bone +N (same N, 1–8) → Invisibility Cloak +N.",
      he: "מתכון Chaos Machine: Jewel of Chaos ×1 + Scroll of Archangel +N + Blood Bone +N (אותו N, 1–8) → Invisibility Cloak +N.",
    },
    {
      type: "mix",
      title: { en: "Invisibility Cloak", he: "Invisibility Cloak" },
      zen: {
        en: "See Zen table below by cloak level",
        he: "ראו טבלת Zen למטה לפי רמת ה־Cloak",
      },
      rate: {
        en: "80% all VIP tiers",
        he: "80% לכל דרגות VIP",
      },
      fail: {
        en: "Materials are destroyed",
        he: "החומרים מושמדים",
      },
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        {
          img: guideImg.scrollArchangel,
          name: "Scroll of Archangel",
          level: "+N",
        },
        { img: guideImg.bloodBone, name: "Blood Bone", level: "+N" },
      ],
      result: {
        img: guideImg.cloakInvis,
        name: "Invisibility Cloak",
        level: "+N",
      },
    },
    {
      type: "p",
      en: "Success rate: 80% for all VIP tiers. Failure destroys the materials.",
      he: "שיעור הצלחה: 80% לכל דרגות VIP. כישלון משמיד את החומרים.",
    },
    {
      type: "table",
      columns: [
        { key: "n", en: "Cloak +N", he: "Cloak +N" },
        { key: "zen", en: "Zen cost", he: "עלות Zen" },
      ],
      rows: BC_CLOAK_ZEN.map(([n, zen]) => ({
        n: `+${n}`,
        zen: fmtZen(zen),
      })),
    },
    {
      type: "h",
      en: "Level tiers — DW / DK / FE / SU",
      he: "דרגות רמה — DW / DK / FE / SU",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "BC1", levels: "15–80" },
        { tier: "BC2", levels: "81–130" },
        { tier: "BC3", levels: "131–180" },
        { tier: "BC4", levels: "181–230" },
        { tier: "BC5", levels: "231–280" },
        { tier: "BC6", levels: "281–330" },
        { tier: "BC7", levels: "331–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters enter BC8.",
      he: "דמויות מחלקה שלישית נכנסות ל־BC8.",
    },
    {
      type: "h",
      en: "Level tiers — MG / DL / RF",
      he: "דרגות רמה — MG / DL / RF",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "BC1", levels: "10–60" },
        { tier: "BC2", levels: "61–110" },
        { tier: "BC3", levels: "111–160" },
        { tier: "BC4", levels: "161–210" },
        { tier: "BC5", levels: "211–260" },
        { tier: "BC6", levels: "261–310" },
        { tier: "BC7", levels: "311–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters enter BC8.",
      he: "דמויות מחלקה שלישית נכנסות ל־BC8.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Fight through monster waves, destroy the bridge and gate, reach the Archangel statue, pick up Absolute Weapon, and deliver it to Archangel King before time runs out.",
      he: "לחמו בגלי מפלצות, השמידו את הגשר והשער, הגיעו לפסל Archangel, הרימו Absolute Weapon ומסרו ל־Archangel King לפני שנגמר הזמן.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Deliver Absolute Weapon to Archangel King to finish the run successfully for your party.",
      he: "מסרו Absolute Weapon ל־Archangel King כדי לסיים את הריצה בהצלחה לפארטי שלכם.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Experience and Zen according to the event reward tables for your tier. Random event reward chests / bags may drop — percentages are not listed here; check live bags and Event Time notices.",
      he: "ניסיון ו־Zen לפי טבלאות התגמול של דרגתכם. תיבות / תיקי תגמול אקראיים של האירוע עשויים ליפול — אחוזים לא מפורטים כאן; בדקו bags חיים והודעות Event Time.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "A party is strongly recommended. Entry items are consumed. Enter only during the open window shown on /events or Event Time.",
      he: "פארטי מומלץ מאוד. פריטי כניסה נצרכים. היכנסו רק בחלון הפתוח שמוצג ב־/events או ב־Event Time.",
    },
  ],

  "devil-square": [
    ...timedPveSharedNotes(),
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Devil Square is a timed survival event. Enter the square and survive monster waves for experience and loot.",
      he: "Devil Square הוא אירוע הישרדות מתוזמן. היכנסו לריבוע ושרדו גלי מפלצות לניסיון ולוט.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Daily starts (Asia/Jerusalem / server time): 02:00, 06:00, 10:00, 14:00, 18:00, 22:00.",
      he: "התחלות יומיות (Asia/Jerusalem / שעון השרת): 02:00, 06:00, 10:00, 14:00, 18:00, 22:00.",
    },
    {
      type: "p",
      en: "Phases: Warning 5 · Notify 1 · Event 20 · Close 4 minutes. Check /events or Event Time for the live status.",
      he: "שלבים: Warning 5 · Notify 1 · Event 20 · Close 4 דקות. בדקו /events או Event Time לסטטוס החי.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Noria — Charon (around 171, 105).",
      he: "Noria — Charon (בסביבות 171, 105).",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Devil's Invitation (14,19) with the correct level for your tier, OR Devil Square Ticket (13,46). The invitation or ticket is consumed on entry.",
      he: "Devil's Invitation (14,19) ברמה המתאימה לדרגה שלכם, או Devil Square Ticket (13,46). ההזמנה או הכרטיס נצרכים בכניסה.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Craft or obtain Devil's Invitation +N / Devil Square Ticket. 2) Go to Charon in Noria. 3) Speak to Charon when Devil Square is open on Event Time.",
      he: "1) צרו או השיגו Devil's Invitation +N / Devil Square Ticket. 2) הגיעו ל־Charon ב־Noria. 3) דברו עם Charon כש־Devil Square פתוח ב־Event Time.",
    },
    {
      type: "h",
      en: "Ticket / Chaos Machine",
      he: "כרטיס / Chaos Machine",
    },
    {
      type: "p",
      en: "Chaos Machine recipe: Jewel of Chaos + Devil's Eye +N + Devil's Key +N → Devil's Invitation +N.",
      he: "מתכון Chaos Machine: Jewel of Chaos + Devil's Eye +N + Devil's Key +N → Devil's Invitation +N.",
    },
    {
      type: "mix",
      title: { en: "Devil's Invitation", he: "Devil's Invitation" },
      zen: {
        en: "See Zen table below by invitation level",
        he: "ראו טבלת Zen למטה לפי רמת ההזמנה",
      },
      rate: {
        en: "+1–+4 = 80% · +5–+7 = 70% (all VIP)",
        he: "+1–+4 = 80% · +5–+7 = 70% (כל VIP)",
      },
      fail: {
        en: "Materials are destroyed",
        he: "החומרים מושמדים",
      },
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        { img: guideImg.devilsEye, name: "Devil's Eye", level: "+N" },
        { img: guideImg.devilsKey, name: "Devil's Key", level: "+N" },
      ],
      result: {
        img: guideImg.devilsInvite,
        name: "Devil's Invitation",
        level: "+N",
      },
    },
    {
      type: "p",
      en: "Success rates (all VIP tiers): +1 to +4 = 80%, +5 to +7 = 70%. Failure destroys materials.",
      he: "שיעורי הצלחה (כל דרגות VIP): +1 עד +4 = 80%, +5 עד +7 = 70%. כישלון משמיד חומרים.",
    },
    {
      type: "table",
      columns: [
        { key: "n", en: "Invitation +N", he: "Invitation +N" },
        { key: "zen", en: "Zen cost", he: "עלות Zen" },
        { key: "rate", en: "Success", he: "הצלחה" },
      ],
      rows: DS_INVITE_ZEN.map(([n, zen]) => ({
        n: `+${n}`,
        zen: fmtZen(zen),
        rate: n <= 4 ? "80%" : "70%",
      })),
    },
    {
      type: "h",
      en: "Level tiers — MG / DL / RF",
      he: "דרגות רמה — MG / DL / RF",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "DS1", levels: "10–110" },
        { tier: "DS2", levels: "111–160" },
        { tier: "DS3", levels: "161–210" },
        { tier: "DS4", levels: "211–260" },
        { tier: "DS5", levels: "261–310" },
        { tier: "DS6", levels: "311–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters enter DS7.",
      he: "דמויות מחלקה שלישית נכנסות ל־DS7.",
    },
    {
      type: "h",
      en: "Level tiers — DW / DK / FE / SU",
      he: "דרגות רמה — DW / DK / FE / SU",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "DS1", levels: "15–130" },
        { tier: "DS2", levels: "131–180" },
        { tier: "DS3", levels: "181–230" },
        { tier: "DS4", levels: "231–280" },
        { tier: "DS5", levels: "281–330" },
        { tier: "DS6", levels: "331–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters enter DS7.",
      he: "דמויות מחלקה שלישית נכנסות ל־DS7.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Monster waves spawn inside Devil Square. Survive and clear as many as you can until the event ends.",
      he: "גלי מפלצות מופיעים בתוך Devil Square. שרדו ונקו כמה שיותר עד סיום האירוע.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Stay alive through the waves for the full event window. There is no separate delivery objective — survival and kills are the goal.",
      he: "הישארו בחיים לאורך הגלים לכל אורך חלון האירוע. אין יעד מסירה נפרד — הישרדות והריגות הן המטרה.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Experience, Zen, and event loot / bags according to the Devil Square reward tables. Drop percentages are not listed here.",
      he: "ניסיון, Zen ולוט / תיקים לפי טבלאות התגמול של Devil Square. אחוזי דרופ לא מפורטים כאן.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Bring potions and a spare invitation. Enter during the open window only. Match invitation level to your character tier.",
      he: "הביאו פושנים והזמנה גיבוי. היכנסו רק בחלון הפתוח. התאימו את רמת ההזמנה לדרגת הדמות.",
    },
  ],

  "chaos-castle": [
    ...timedPveSharedNotes(),
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Chaos Castle is a survival PvE event. Use your entry item when the event is open, fight inside, and stay alive. Minimum players required: 1.",
      he: "Chaos Castle הוא אירוע הישרדות PvE. השתמשו בפריט הכניסה כשהאירוע פתוח, לחמו בפנים והישארו בחיים. מינימום שחקנים: 1.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Daily starts (Asia/Jerusalem / server time): 01:00, 05:00, 09:00, 13:00, 17:00, 21:00.",
      he: "התחלות יומיות (Asia/Jerusalem / שעון השרת): 01:00, 05:00, 09:00, 13:00, 17:00, 21:00.",
    },
    {
      type: "p",
      en: "Phases: Warning 5 · Notify 1 · Event 10 · Close 4 minutes. Check /events or Event Time.",
      he: "שלבים: Warning 5 · Notify 1 · Event 10 · Close 4 דקות. בדקו /events או Event Time.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "There is no entry NPC for Chaos Castle. When the event is open, use Armor of Guardsman or Open Access Ticket from your inventory to enter.",
      he: "אין NPC כניסה ל־Chaos Castle. כשהאירוע פתוח, השתמשו ב־Armor of Guardsman או Open Access Ticket מהאינוונטרי כדי להיכנס.",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Armor of Guardsman (13,29) OR Open Access Ticket (13,121). There is no Chaos Machine craft for Armor of Guardsman — you need the item as-is. Entry items are consumed on use when the event is open.",
      he: "Armor of Guardsman (13,29) או Open Access Ticket (13,121). אין קראפט ב־Chaos Machine ל־Armor of Guardsman — נדרש הפריט כמו שהוא. פריטי כניסה נצרכים בשימוש כשהאירוע פתוח.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Hold Armor of Guardsman or Open Access Ticket. 2) Wait until Event Time shows Chaos Castle open. 3) Use the item from inventory — you are moved into Chaos Castle. No NPC click required.",
      he: "1) החזיקו Armor of Guardsman או Open Access Ticket. 2) המתינו עד ש־Event Time מציג Chaos Castle פתוח. 3) השתמשו בפריט מהאינוונטרי — תועברו לתוך Chaos Castle. אין צורך בלחיצה על NPC.",
    },
    {
      type: "h",
      en: "Level tiers — MG / DL / RF",
      he: "דרגות רמה — MG / DL / RF",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "CC1", levels: "10–29" },
        { tier: "CC2", levels: "30–99" },
        { tier: "CC3", levels: "100–159" },
        { tier: "CC4", levels: "160–219" },
        { tier: "CC5", levels: "220–279" },
        { tier: "CC6", levels: "280–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters enter CC7.",
      he: "דמויות מחלקה שלישית נכנסות ל־CC7.",
    },
    {
      type: "h",
      en: "Level tiers — DW / DK / FE / SU",
      he: "דרגות רמה — DW / DK / FE / SU",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "CC1", levels: "10–49" },
        { tier: "CC2", levels: "50–119" },
        { tier: "CC3", levels: "120–179" },
        { tier: "CC4", levels: "180–239" },
        { tier: "CC5", levels: "240–299" },
        { tier: "CC6", levels: "300–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters enter CC7.",
      he: "דמויות מחלקה שלישית נכנסות ל־CC7.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Survive inside Chaos Castle while monsters (and other players in the same arena) fight. Stay alive until the event ends.",
      he: "שרדו בתוך Chaos Castle בזמן שמפלצות (ושחקנים אחרים באותה זירה) נלחמים. הישארו בחיים עד סיום האירוע.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Survive the event window. Rewards are granted according to performance / event tables.",
      he: "שרדו את חלון האירוע. תגמולים ניתנים לפי ביצוע / טבלאות האירוע.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Experience and event reward bags / chests. Drop percentages are not listed here.",
      he: "ניסיון ותיקי / תיבות תגמול של האירוע. אחוזי דרופ לא מפורטים כאן.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Do not invent a Chaos Machine recipe for Armor of Guardsman — obtain the item from play or shop sources that drop/sell it. Use the item only when the event is open.",
      he: "אל תמציאו מתכון Chaos Machine ל־Armor of Guardsman — השיגו את הפריט ממקורות משחק / חנות שנותנים אותו. השתמשו בפריט רק כשהאירוע פתוח.",
    },
  ],

  illusion: [
    ...timedPveSharedNotes(),
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Illusion Temple is a team event with relic / statue objectives. At least 2 players are required to start. Completing the event may grant a rare Fenrir-related reward chance (server rate configured as 5 — treat it as uncommon, not guaranteed).",
      he: "Illusion Temple הוא אירוע קבוצתי עם יעדי relic / פסל. נדרשים לפחות 2 שחקנים להתחלה. סיום האירוע עשוי לתת הזדמנות נדירה לתגמול הקשור ל־Fenrir (שיעור שרת מוגדר כ־5 — התייחסו לזה כלא־נפוץ, לא מובטח).",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Starts at :30 on hours 01, 05, 09, 13, 17, 21 (Asia/Jerusalem / server time). Example: 01:30, 05:30, and so on.",
      he: "מתחיל ב־:30 בשעות 01, 05, 09, 13, 17, 21 (Asia/Jerusalem / שעון השרת). לדוגמה: 01:30, 05:30 וכן הלאה.",
    },
    {
      type: "p",
      en: "Phases: Warning 5 · Notify 1 · Event 15 · Close 4 minutes. Check /events or Event Time.",
      he: "שלבים: Warning 5 · Notify 1 · Event 15 · Close 4 דקות. בדקו /events או Event Time.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Mirage in Lorencia (around 130, 121). Also available in Elbeland (around 42, 242).",
      he: "Mirage ב־Lorencia (בסביבות 130, 121). זמין גם ב־Elbeland (בסביבות 42, 242).",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Scroll of Blood with the correct level for your tier, OR Illusion Temple Ticket (13,61). Scroll / ticket is consumed on entry. Minimum 2 players.",
      he: "Scroll of Blood ברמה המתאימה לדרגה שלכם, או Illusion Temple Ticket (13,61). מגילה / כרטיס נצרכים בכניסה. מינימום 2 שחקנים.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Prepare Scroll of Blood +N or Illusion Temple Ticket. 2) Go to Mirage in Lorencia or Elbeland. 3) Speak to Mirage when Illusion Temple is open and you have enough players.",
      he: "1) הכינו Scroll of Blood +N או Illusion Temple Ticket. 2) הגיעו ל־Mirage ב־Lorencia או Elbeland. 3) דברו עם Mirage כש־Illusion Temple פתוח ויש מספיק שחקנים.",
    },
    {
      type: "h",
      en: "Ticket / Chaos Machine",
      he: "כרטיס / Chaos Machine",
    },
    {
      type: "p",
      en: "Chaos Machine recipe: Jewel of Chaos + Old Scroll +N + Illusion Sorcerer Covenant +N → Scroll of Blood +N.",
      he: "מתכון Chaos Machine: Jewel of Chaos + Old Scroll +N + Illusion Sorcerer Covenant +N → Scroll of Blood +N.",
    },
    {
      type: "mix",
      title: { en: "Scroll of Blood", he: "Scroll of Blood" },
      zen: {
        en: "See Zen table below by scroll level",
        he: "ראו טבלת Zen למטה לפי רמת המגילה",
      },
      rate: {
        en: "70% all VIP tiers",
        he: "70% לכל דרגות VIP",
      },
      fail: {
        en: "Materials are destroyed",
        he: "החומרים מושמדים",
      },
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        { img: guideImg.oldScroll, name: "Old Scroll", level: "+N" },
        {
          img: guideImg.illusionCovenant,
          name: "Illusion Sorcerer Covenant",
          level: "+N",
        },
      ],
      result: {
        img: guideImg.scrollBlood,
        name: "Scroll of Blood",
        level: "+N",
      },
    },
    {
      type: "p",
      en: "Success rate: 70% for all VIP tiers. Failure loses materials.",
      he: "שיעור הצלחה: 70% לכל דרגות VIP. כישלון מאבד חומרים.",
    },
    {
      type: "table",
      columns: [
        { key: "n", en: "Scroll +N", he: "Scroll +N" },
        { key: "zen", en: "Zen cost", he: "עלות Zen" },
      ],
      rows: IT_SCROLL_ZEN.map(([n, zen]) => ({
        n: `+${n}`,
        zen: fmtZen(zen),
      })),
    },
    {
      type: "h",
      en: "Level tiers",
      he: "דרגות רמה",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "levels", en: "Level range", he: "טווח רמות" },
      ],
      rows: [
        { tier: "IT1", levels: "220–270" },
        { tier: "IT2", levels: "271–320" },
        { tier: "IT3", levels: "321–350" },
        { tier: "IT4", levels: "351–380" },
        { tier: "IT5", levels: "381–MAX" },
      ],
    },
    {
      type: "p",
      en: "3rd class characters (ChangeUp ≥ 2) join IT5. There is no separate IT6 tier on this server.",
      he: "דמויות מחלקה שלישית (ChangeUp ≥ 2) נכנסות ל־IT5. אין דרגת IT6 נפרדת בשרת זה.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Teams compete for relic / statue objectives inside Illusion Temple until the event window ends.",
      he: "קבוצות מתחרות על יעדי relic / פסל בתוך Illusion Temple עד סיום חלון האירוע.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Complete the temple objectives with your team before time expires.",
      he: "השלימו את יעדי המקדש עם הקבוצה לפני שנגמר הזמן.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Event rewards for completing Illusion Temple, including a rare Fenrir-related chance. Exact drop percentages are not published here.",
      he: "תגמולי אירוע על השלמת Illusion Temple, כולל הזדמנות נדירה הקשורה ל־Fenrir. אחוזי דרופ מדויקים לא מפורסמים כאן.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Bring at least one teammate. Match scroll level to your tier. Enter only when Event Time shows Illusion Temple open.",
      he: "הביאו לפחות שותף אחד. התאימו את רמת המגילה לדרגה. היכנסו רק כש־Event Time מציג Illusion Temple פתוח.",
    },
  ],

  imperial: [
    ...timedPveSharedNotes(),
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Imperial Guardian is a timed day-of-week event. It is separate from the custom Dungeon System Imperial Relic instance.",
      he: "Imperial Guardian הוא אירוע מתוזמן לפי יום בשבוע. הוא נפרד ממופע Imperial Relic במערכת המבוכים המותאמת.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "table",
      columns: [
        { key: "day", en: "Day", he: "יום" },
        { key: "time", en: "Start", he: "התחלה" },
      ],
      rows: [
        {
          day: { en: "Sunday", he: "יום ראשון" },
          time: "20:00",
        },
        {
          day: { en: "Tuesday", he: "יום שלישי" },
          time: "20:00",
        },
        {
          day: { en: "Friday", he: "יום שישי" },
          time: "19:00",
        },
      ],
    },
    {
      type: "p",
      en: "Phases: Notify 1 · Entry 5 · Event 10 · Close 1 minutes. Asia/Jerusalem / server time. Confirm on /events or Event Time.",
      he: "שלבים: Notify 1 · Entry 5 · Event 10 · Close 1 דקות. Asia/Jerusalem / שעון השרת. אשרו ב־/events או Event Time.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Jerry / Jerint the Assistant on Event Square (around 237, 25) and in Devias (around 229, 221).",
      he: "Jerry / Jerint the Assistant ב־Event Square (בסביבות 237, 25) וב־Devias (בסביבות 229, 221).",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Day keys / Sunday key, OR tickets (13,126) / (13,127). Use the key that matches the scheduled day.",
      he: "מפתחות יום / מפתח Sunday, או כרטיסים (13,126) / (13,127). השתמשו במפתח המתאים ליום המתוזמן.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Hold the correct day key or ticket. 2) Go to Jerry / Jerint the Assistant. 3) Speak to the NPC during the Sunday / Tuesday / Friday open window.",
      he: "1) החזיקו מפתח יום או כרטיס נכון. 2) הגיעו ל־Jerry / Jerint the Assistant. 3) דברו עם ה־NPC בחלון הפתוח של יום ראשון / שלישי / שישי.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Clear Imperial Guardian stages within the timed entry and event windows.",
      he: "נקו שלבי Imperial Guardian בתוך חלונות הכניסה והאירוע המתוזמנים.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Finish the Guardian stages before Close. Leave when the event ends.",
      he: "סיימו את שלבי ה־Guardian לפני Close. צאו כשהאירוע מסתיים.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Event reward bags / chests from Imperial Guardian runs. Drop percentages are not listed here.",
      he: "תיקי / תיבות תגמול מריצות Imperial Guardian. אחוזי דרופ לא מפורטים כאן.",
    },
    {
      type: "note",
      en: "For daily Relic dungeon instances (including Imperial Relic) see Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
      he: "למופעי מבוך יומיים עם Relic (כולל Imperial Relic) ראו Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Do not confuse this timed event with Dungeon Imperial Relic. Bring the correct day key. Check Event Time on Sunday, Tuesday, and Friday.",
      he: "אל תבלבלו אירוע מתוזמן זה עם Dungeon Imperial Relic. הביאו מפתח יום נכון. בדקו Event Time בימי ראשון, שלישי ושישי.",
    },
  ],

  kanturu: [
    ...timedPveSharedNotes(),
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Kanturu is a timed event with Maya and Nightmare stages. Enter through the Gate NPC when the event is open.",
      he: "Kanturu הוא אירוע מתוזמן עם שלבי Maya ו־Nightmare. היכנסו דרך ה־Gate NPC כשהאירוע פתוח.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Daily (Asia/Jerusalem / server time): 00:00 and 12:00.",
      he: "יומי (Asia/Jerusalem / שעון השרת): 00:00 ו־12:00.",
    },
    {
      type: "p",
      en: "Phases: Warning 5 · Entry 5 · Event 45 · Close 5 minutes. Confirm on /events or Event Time.",
      he: "שלבים: Warning 5 · Entry 5 · Event 45 · Close 5 דקות. אשרו ב־/events או Event Time.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Speak to the Kanturu Gate NPC when the event window is open.",
      he: "דברו עם ה־Gate NPC של Kanturu כשחלון האירוע פתוח.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "When Event Time shows Kanturu open, use the Gate NPC to enter the event stages.",
      he: "כש־Event Time מציג Kanturu פתוח, השתמשו ב־Gate NPC כדי להיכנס לשלבי האירוע.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Progress through Maya and Nightmare stages during the long event window.",
      he: "התקדמו דרך שלבי Maya ו־Nightmare במהלך חלון האירוע הארוך.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Clear the Maya / Nightmare stages before Close.",
      he: "נקו את שלבי Maya / Nightmare לפני Close.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Event bags for Maya / Nightmare stages. For farming Fenrir materials in a daily Relic instance, use the custom Kanturu Relic dungeon — Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
      he: "תיקי אירוע לשלבי Maya / Nightmare. לפארם חומרי Fenrir במופע Relic יומי, השתמשו במבוך Kanturu Relic המותאם — Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "The timed Kanturu event is separate from Dungeon Kanturu Relic. Bring a party for Nightmare stages if possible.",
      he: "אירוע Kanturu המתוזמן נפרד מ־Dungeon Kanturu Relic. הביאו פארטי לשלבי Nightmare אם אפשר.",
    },
  ],

  kalima: [
    {
      type: "note",
      en: "Kalima / Kundun is always-available map content — not a scheduled timed event. There is no hourly calendar for Kundun.",
      he: "Kalima / Kundun הוא תוכן מפה שזמין תמיד — לא אירוע מתוזמן בלוח שעות. אין לוח שעתי ל־Kundun.",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Kalima is open whenever you are ready. Enter via Symbol of Kundun / Lost Map and fight Kundun and related content at your own pace.",
      he: "Kalima פתוח מתי שאתם מוכנים. היכנסו דרך Symbol of Kundun / Lost Map ולחמו ב־Kundun ובתוכן הקשור בקצב שלכם.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "None — always on. For live timed events elsewhere, use /events or Event Time (Asia/Jerusalem / server time).",
      he: "אין — תמיד פתוח. לאירועים מתוזמנים אחרים השתמשו ב־/events או Event Time (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "Use Symbol of Kundun / Lost Map to reach Kalima, then proceed to Kundun when your party is ready.",
      he: "השתמשו ב־Symbol of Kundun / Lost Map כדי להגיע ל־Kalima, ואז המשיכו ל־Kundun כשהפארטי מוכן.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Clear Kalima floors and fight Kundun. This is standard map PvE, not a countdown event.",
      he: "נקו קומות Kalima ולחמו ב־Kundun. זה PvE מפה רגיל, לא אירוע עם ספירה לאחור.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Kundun and Kalima drops follow live bag tables. Percentages are not invented here — check in-game drops.",
      he: "דרופים של Kundun ו־Kalima לפי טבלאות bags חיות. אחוזים לא מומצאים כאן — בדקו דרופים במשחק.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Do not wait for Event Time for Kundun — go when you are geared and ready.",
      he: "אל תחכו ל־Event Time עבור Kundun — לכו כשאתם מצוידים ומוכנים.",
    },
  ],

  invasions: [
    {
      type: "note",
      en: "Live countdowns: /events or in-game Event Time (Asia/Jerusalem / server time).",
      he: "ספירות לאחור חיות: /events או Event Time במשחק (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "World invasions spawn monsters on open maps. When an invasion starts, the server announces it — go to the listed maps and hunt. You typically do not need a ticket; just move to the map and kill the invasion monsters.",
      he: "פלישות עולם מזמנות מפלצות במפות פתוחות. כשפלישה מתחילה השרת מכריז — לכו למפות הרשומות וצודו. בדרך כלל אין צורך בכרטיס; פשוט עברו למפה והרגו את מפלצות הפלישה.",
    },
    {
      type: "h",
      en: "How to participate — Red Dragon",
      he: "איך משתתפים — Red Dragon",
    },
    {
      type: "p",
      en: "Red Dragon starts daily at 00:05, 08:05, and 16:05 (Asia/Jerusalem / server time). When the server message appears, go to Lorencia, Devias, or Noria and kill Red Dragons. The invasion lasts about 30 minutes.",
      he: "Red Dragon מתחיל יומית ב־00:05, 08:05 ו־16:05 (Asia/Jerusalem / שעון השרת). כשמופיעה הודעת השרת, לכו ל־Lorencia, Devias או Noria והרגו Red Dragons. הפלישה נמשכת כ־30 דקות.",
    },
    {
      type: "h",
      en: "How to participate — Golden Invasion / Goldens / Dourados",
      he: "איך משתתפים — Golden Invasion / Goldens / Dourados",
    },
    {
      type: "p",
      en: "Golden Invasion (Goldens / Dourados) starts daily at 04:05, 12:05, and 20:05. Hunt golden monsters and the Great Golden Dragon across many maps including Lorencia, Devias, Noria, and others. Lasts about 30 minutes after the server start message.",
      he: "Golden Invasion (Goldens / Dourados) מתחיל יומית ב־04:05, 12:05 ו־20:05. צודו מפלצות זהב ואת Great Golden Dragon במפות רבות כולל Lorencia, Devias, Noria ואחרות. נמשך כ־30 דקות אחרי הודעת ההתחלה של השרת.",
    },
    {
      type: "h",
      en: "Invasion schedule",
      he: "לוח פלישות",
    },
    {
      type: "table",
      columns: [
        { key: "name", en: "Invasion", he: "פלישה" },
        { key: "when", en: "Schedule", he: "לוח זמנים" },
      ],
      rows: [
        { name: "Skeleton King", when: "15:05" },
        {
          name: "Red Dragon",
          when: {
            en: "00:05 / 08:05 / 16:05 — Lorencia, Devias, Noria (~30 min)",
            he: "00:05 / 08:05 / 16:05 — Lorencia, Devias, Noria (כ־30 דק׳)",
          },
        },
        {
          name: "Golden Invasion / Goldens / Dourados",
          when: {
            en: "04:05 / 12:05 / 20:05 — many maps incl. Lorencia / Devias / Noria (~30 min)",
            he: "04:05 / 12:05 / 20:05 — מפות רבות כולל Lorencia / Devias / Noria (כ־30 דק׳)",
          },
        },
        { name: "White Wizard", when: "18:50" },
        {
          name: "Pouch of Blessing",
          when: { en: "Every 4h at :55", he: "כל 4 שעות ב־:55" },
        },
        {
          name: "White Rabbit",
          when: { en: "Every 2h at :35", he: "כל שעתיים ב־:35" },
        },
        {
          name: "Fire Flame Ghost",
          when: { en: "Every 3h at :45", he: "כל 3 שעות ב־:45" },
        },
        { name: "Medusa", when: "21:05" },
        {
          name: "Ferea",
          when: { en: "Friday 22:30", he: "יום שישי 22:30" },
        },
        { name: "Nix", when: "07:05" },
        { name: "God of Darkness", when: "08:05" },
        { name: "Core Magriffy", when: "09:05" },
        { name: "Lord Silvestre", when: "10:05" },
        { name: "Protector of Acheron", when: "22:05" },
        {
          name: "Evomon",
          when: {
            en: "Monday / Wednesday / Friday 23:05",
            he: "יום שני / רביעי / שישי 23:05",
          },
        },
        { name: "Chicken", when: "00:00" },
        {
          name: "Arka War",
          when: { en: "Tuesday 20:00", he: "יום שלישי 20:00" },
        },
        { name: "Invocation Demons", when: "01:05" },
        { name: "Merchant Zyro", when: "02:05" },
        { name: "Boss Battle Together I", when: "03:05" },
        { name: "Boss Battle Together II", when: "11:05" },
        { name: "Boss Battle Together III", when: "23:05" },
        { name: "Horse", when: "04:05" },
        { name: "Goat", when: "12:05" },
        { name: "Monkey", when: "00:05" },
        { name: "Goblin", when: "13:05" },
        { name: "Silent Invasion", when: "05:05" },
        { name: "Golden Hell Maine", when: "06:05" },
      ],
    },
    {
      type: "note",
      en: "Christmas invasion is disabled on this server.",
      he: "פלישת Christmas כבויה בשרת זה.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Watch the server chat for start messages. Use /events or Event Time so you do not miss Red Dragon and Golden windows. Party up for dangerous bosses.",
      he: "עקבו אחרי צ׳אט השרת להודעות התחלה. השתמשו ב־/events או Event Time כדי לא לפספס חלונות Red Dragon ו־Golden. התאגדו בפארטי לבוסים מסוכנים.",
    },
  ],

  "boss-together": [
    {
      type: "note",
      en: "Live countdowns: /events or Event Time (Asia/Jerusalem / server time).",
      he: "ספירות לאחור חיות: /events או Event Time (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Boss Battle Together is a cooperative boss invasion on the Battle Boss map. Players gather to kill event bosses and monsters during a short open window for jewel and box drops.",
      he: "Boss Battle Together הוא פלישת בוסים שיתופית במפת Battle Boss. שחקנים מתאספים להרוג בוסי אירוע ומפלצות בחלון פתוח קצר לקבלת אבנים ותיבות.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "table",
      columns: [
        { key: "wave", en: "Wave", he: "גל" },
        { key: "time", en: "Start", he: "התחלה" },
        { key: "bosses", en: "Bosses", he: "בוסים" },
      ],
      rows: [
        {
          wave: "Boss Battle Together I",
          time: "03:05",
          bosses: "[Boss] Medusa + [Boss] Nix",
        },
        {
          wave: "Boss Battle Together II",
          time: "11:05",
          bosses: "[Boss] Selupan + [Boss] Kundun",
        },
        {
          wave: "Boss Battle Together III",
          time: "23:05",
          bosses: {
            en: "[Boss] Nightmare + [Boss] God of Darkness",
            he: "[Boss] Nightmare + [Boss] God of Darkness",
          },
        },
      ],
    },
    {
      type: "p",
      en: "Each wave is active for about 10 minutes after start (Asia/Jerusalem / server time).",
      he: "כל גל פעיל כ־10 דקות אחרי ההתחלה (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "p",
      en: "Appeared means Event Time shows the event is currently running (the countdown reached 0).",
      he: "Appeared אומר ש־Event Time מציג שהאירוע רץ כרגע (הספירה לאחור הגיעה ל־0).",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Level 400+. No ticket is required for the NPC entry path described below. (A Boss Battle Ticket item exists in the game, but this entry method does not use it — do not wait for a ticket.)",
      he: "רמה 400 ומעלה. אין צורך בכרטיס לנתיב הכניסה דרך ה־NPC המתואר למטה. (קיים פריט Boss Battle Ticket במשחק, אך שיטת הכניסה הזו לא משתמשת בו — אל תחכו לכרטיס.)",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Event Square map — NPC \"Boss Battle Event\" around 225, 31.",
      he: "מפת Event Square — NPC בשם \"Boss Battle Event\" בסביבות 225, 31.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Type /move Event Square (or walk to the Event Square map). 2) Find the NPC \"Boss Battle Event\" around coordinates 225, 31. 3) Speak / click the NPC — it teleports you to the Battle Boss map (level 400+ required). 4) Important: the Event Time warp button does NOT teleport you for this event (it is disabled) — you must use the NPC.",
      he: "1) הקלידו /move Event Square (או לכו ברגל למפת Event Square). 2) מצאו את ה־NPC \"Boss Battle Event\" בסביבות הקואורדינטות 225, 31. 3) דברו / לחצו על ה־NPC — הוא מטלפרט אתכם למפת Battle Boss (נדרשת רמה 400+). 4) חשוב: כפתור ה־warp ב־Event Time לא מטלפרט לאירוע זה (מושבת) — חובה להשתמש ב־NPC.",
    },
    {
      type: "h",
      en: "Party",
      he: "פארטי",
    },
    {
      type: "p",
      en: "Party is optional but recommended. The server does not force a party to enter.",
      he: "פארטי אופציונלי אך מומלץ. השרת לא מחייב פארטי כדי להיכנס.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Wave I: [Boss] Medusa and [Boss] Nix. Wave II: [Boss] Selupan and [Boss] Kundun. Wave III: [Boss] Nightmare and [Boss] God of Darkness. Kill bosses and monsters during the open window.",
      he: "גל I: [Boss] Medusa ו־[Boss] Nix. גל II: [Boss] Selupan ו־[Boss] Kundun. גל III: [Boss] Nightmare ו־[Boss] God of Darkness. הרגו בוסים ומפלצות במהלך החלון הפתוח.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Kill as many bosses and monsters as you can while the invasion is active. When finished (about 10 minutes), monsters despawn.",
      he: "הרגו כמה שיותר בוסים ומפלצות בזמן שהפלישה פעילה. בסיום (כ־10 דקות) המפלצות נעלמות.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Event boss drops such as jewels and boxes. These bags do not grant currency. Drop percentages are not listed here.",
      he: "דרופים מבוסי האירוע כגון אבנים ותיבות. התיקים האלה לא נותנים מטבע. אחוזי דרופ לא מפורטים כאן.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Be on Event Square before the start time. Do not rely on the Event Time warp button. Bring potions and a party for Wave II / III bosses.",
      he: "היו ב־Event Square לפני שעת ההתחלה. אל תסמכו על כפתור ה־warp ב־Event Time. הביאו פושנים ופארטי לבוסים של גל II / III.",
    },
  ],

  "arka-war": [
    {
      type: "note",
      en: "Live countdowns: /events or Event Time (Asia/Jerusalem / server time).",
      he: "ספירות לאחור חיות: /events או Event Time (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Arka War is a guild capture event that runs together with ReiDoMU. Coordinate with your guild to fight for control during the open window.",
      he: "Arka War הוא אירוע כיבוש גילדות שרץ יחד עם ReiDoMU. תאמו עם הגילדה שלכם כדי להילחם על השליטה בחלון הפתוח.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Tuesday 20:00 (Asia/Jerusalem / server time), together with ReiDoMU. Confirm on /events or Event Time.",
      he: "יום שלישי 20:00 (Asia/Jerusalem / שעון השרת), יחד עם ReiDoMU. אשרו ב־/events או Event Time.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "Join with your guild when Event Time shows Arka War / ReiDoMU open. Follow guild officers for staging and fight orders.",
      he: "הצטרפו עם הגילדה כש־Event Time מציג Arka War / ReiDoMU פתוח. עקבו אחרי קציני הגילדה להיערכות ולהוראות קרב.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Guilds compete to capture and hold Arka / ReiDoMU objectives during the event window.",
      he: "גילדות מתחרות לכיבוש והחזקת יעדי Arka / ReiDoMU במהלך חלון האירוע.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Winning guild members / top character reward: 50 Credits.",
      he: "תגמול לחברי הגילדה המנצחת / דמות מובילה: 50 Credits.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Organize in Discord / guild chat before Tuesday 20:00. Bring siege-ready gear and stay with your guild group.",
      he: "ארגנו ב־Discord / צ׳אט גילדה לפני יום שלישי 20:00. הביאו ציוד מוכן למצור והישארו עם קבוצת הגילדה.",
    },
  ],

  "double-goer": [
    {
      type: "note",
      en: "Live status: /events or Event Time (Asia/Jerusalem / server time).",
      he: "סטטוס חי: /events או Event Time (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Double Goer is enabled on Mu Beginning. Clear the Double Goer instance with the required entry items. It is separate from custom Dungeon System Relics.",
      he: "Double Goer מופעל ב־Mu Beginning. נקו את מופע Double Goer עם פריטי הכניסה הנדרשים. נפרד ממערכת Dungeon Relics המותאמת.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Not an hourly fixed pattern like Blood Castle / Devil Square. Always check Event Time or the website Events page for the current window.",
      he: "לא תבנית שעתית קבועה כמו Blood Castle / Devil Square. תמיד בדקו Event Time או דף Events באתר לחלון הנוכחי.",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "Lugard on Elbeland (around 49, 216) and on Event Square (around 238, 30). Some UIs may show the name Sartigan — prefer Lugard as the in-world NPC name.",
      he: "Lugard ב־Elbeland (בסביבות 49, 216) וב־Event Square (בסביבות 238, 30). חלק מהממשקים עשויים להציג את השם Sartigan — העדיפו Lugard כשם ה־NPC בעולם.",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Entry items: (14,111) / (13,125) / (14,125). Bring the correct item for your run.",
      he: "פריטי כניסה: (14,111) / (13,125) / (14,125). הביאו את הפריט הנכון לריצה שלכם.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "1) Hold a Double Goer entry item. 2) Go to Lugard in Elbeland or Event Square. 3) Speak to Lugard when Event Time shows Double Goer available.",
      he: "1) החזיקו פריט כניסה ל־Double Goer. 2) הגיעו ל־Lugard ב־Elbeland או Event Square. 3) דברו עם Lugard כש־Event Time מציג Double Goer זמין.",
    },
    {
      type: "note",
      en: "Separate from custom Dungeon System Relics — Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
      he: "נפרד ממערכת Dungeon Relics — Systems → Dungeon System (/guides?cat=systems&topic=dungeons).",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Do not rely on a memorized hourly clock — always verify on Event Time before spending an entry item.",
      he: "אל תסמכו על שעון שעתי מהזיכרון — תמיד אמתו ב־Event Time לפני שמוציאים פריט כניסה.",
    },
  ],

  "castle-siege": [
    {
      type: "note",
      en: "Castle Siege runs on the Castle Siege (CS) realm only — not on the normal game realm.",
      he: "Castle Siege רץ על רילם Castle Siege (CS) בלבד — לא על הרילם הרגיל של המשחק.",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Classic guild Castle Siege on the CS realm. Guilds register, prepare, and fight for castle control during the siege windows.",
      he: "Castle Siege קלאסי לגילדות על רילם ה־CS. גילדות נרשמות, מתכוננות ונלחמות על שליטת הטירה בחלונות המצור.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Registration and siege windows follow the CS realm calendar. Do not invent a full weekly siege timetable here — always check Event Time on the CS server and /events (Asia/Jerusalem / server time).",
      he: "חלונות רישום ומצור לפי לוח רילם ה־CS. אין לוח מצור שבועי מלא מומצא כאן — תמיד בדקו Event Time על שרת ה־CS ואת /events (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "Connect to the CS realm with your guild during registration / siege windows shown on Event Time.",
      he: "התחברו לרילם ה־CS עם הגילדה בחלונות הרישום / המצור שמוצגים ב־Event Time.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Coordinate registration with guild officers early. Watch Event Time on CS for the next notice and siege phases.",
      he: "תאמו רישום עם קציני הגילדה מראש. עקבו אחרי Event Time ב־CS לשלב ההודעה הבא ולשלבים של המצור.",
    },
  ],

  crywolf: [
    {
      type: "note",
      en: "Crywolf runs on the Castle Siege (CS) realm only.",
      he: "Crywolf רץ על רילם Castle Siege (CS) בלבד.",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Crywolf is a guild defense event on the CS realm. Defend the Crywolf altar and occupation objectives during the open window.",
      he: "Crywolf הוא אירוע הגנה לגילדות על רילם ה־CS. הגנו על מזבח Crywolf ועל יעדי הכיבוש בחלון הפתוח.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Wednesday and Saturday at 20:30 (Asia/Jerusalem / server time). Confirm on /events or Event Time on the CS realm.",
      he: "יום רביעי ושבת ב־20:30 (Asia/Jerusalem / שעון השרת). אשרו ב־/events או Event Time על רילם ה־CS.",
    },
    {
      type: "h",
      en: "How to complete",
      he: "איך מסיימים",
    },
    {
      type: "p",
      en: "Defend the Crywolf altar with your guild until the event ends successfully.",
      he: "הגנו על מזבח Crywolf עם הגילדה עד שהאירוע מסתיים בהצלחה.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Be online on CS before 20:30 on Wednesday and Saturday. Assign altar defenders in advance.",
      he: "היו מחוברים ל־CS לפני 20:30 בימי רביעי ושבת. שייכו מגיני מזבח מראש.",
    },
  ],

  "castle-deep": [
    {
      type: "note",
      en: "Castle Deep runs on the Castle Siege (CS) realm only.",
      he: "Castle Deep רץ על רילם Castle Siege (CS) בלבד.",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Castle Deep is timed monster / reward content on the CS realm during daily open windows.",
      he: "Castle Deep הוא תוכן מפלצות / תגמולים מתוזמן על רילם ה־CS בחלונות יומיים פתוחים.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Daily at 04:00, 12:00, and 20:00 (Asia/Jerusalem / server time). Confirm on /events or Event Time on CS.",
      he: "יומי ב־04:00, 12:00 ו־20:00 (Asia/Jerusalem / שעון השרת). אשרו ב־/events או Event Time ב־CS.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "Join on the CS server when Event Time shows Castle Deep open.",
      he: "הצטרפו על שרת ה־CS כש־Event Time מציג Castle Deep פתוח.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Set an alarm for 04:00 / 12:00 / 20:00 if you farm Castle Deep regularly.",
      he: "הגדירו תזכורת ל־04:00 / 12:00 / 20:00 אם אתם חוקקים Castle Deep באופן קבוע.",
    },
  ],

  "moss-merchant": [
    {
      type: "note",
      en: "Live countdowns: /events or Event Time (Asia/Jerusalem / server time).",
      he: "ספירות לאחור חיות: /events או Event Time (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "Moss Merchant is a timed merchant spawn. When Moss appears, visit the NPC to trade while available.",
      he: "Moss Merchant הוא הופעת סוחר מתוזמנת. כש־Moss מופיע, בקרו אצל ה־NPC לסחור בזמן שהוא זמין.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "Appears at 02:00, 08:00, 14:00, and 20:00 (Asia/Jerusalem / server time).",
      he: "מופיע ב־02:00, 08:00, 14:00 ו־20:00 (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "Where to go",
      he: "לאן ללכת",
    },
    {
      type: "p",
      en: "NPC Moss on Elbeland (around 22, 225) when spawned.",
      he: "NPC Moss ב־Elbeland (בסביבות 22, 225) כשהוא מופיע.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך ניגשים",
    },
    {
      type: "p",
      en: "When Event Time shows Moss Merchant active, go to Elbeland ~22, 225 and speak to Moss to trade.",
      he: "כש־Event Time מציג Moss Merchant פעיל, לכו ל־Elbeland ~22, 225 ו־דברו עם Moss לסחור.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Moss is only available during spawn windows — trade quickly before despawn.",
      he: "Moss זמין רק בחלונות ההופעה — סחרו במהירות לפני שהוא נעלם.",
    },
  ],

  tvt: [
    {
      type: "note",
      en: "Live countdowns: /events or Event Time (Asia/Jerusalem / server time).",
      he: "ספירות לאחור חיות: /events או Event Time (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "TvT (Team vs Team) is scheduled team PvP. No entry item is required — join when the event is open.",
      he: "TvT (Team vs Team) הוא PvP קבוצתי מתוזמן. אין פריט כניסה נדרש — הצטרפו כשהאירוע פתוח.",
    },
    {
      type: "h",
      en: "Schedule",
      he: "לוח זמנים",
    },
    {
      type: "p",
      en: "06:54, 14:05, and 22:05 (Asia/Jerusalem / server time).",
      he: "06:54, 14:05 ו־22:05 (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "Requirements",
      he: "דרישות",
    },
    {
      type: "p",
      en: "Minimum level 400. No entry item.",
      he: "רמה מינימלית 400. אין פריט כניסה.",
    },
    {
      type: "h",
      en: "How to enter",
      he: "איך נכנסים",
    },
    {
      type: "p",
      en: "Join when Event Time shows TvT open. Follow the in-game join / team prompts for your side.",
      he: "הצטרפו כש־Event Time מציג TvT פתוח. עקבו אחרי הנחיות ההצטרפות / הקבוצה במשחק לצד שלכם.",
    },
    {
      type: "h",
      en: "What happens inside",
      he: "מה קורה בפנים",
    },
    {
      type: "p",
      en: "Two teams fight in PvP for the duration of the TvT window.",
      he: "שתי קבוצות נלחמות ב־PvP לאורך חלון ה־TvT.",
    },
    {
      type: "h",
      en: "Rewards",
      he: "תגמולים",
    },
    {
      type: "p",
      en: "Character reward: 50 Credits.",
      he: "תגמול לדמות: 50 Credits.",
    },
    {
      type: "h",
      en: "Tips",
      he: "טיפים",
    },
    {
      type: "p",
      en: "Be online a few minutes before 06:54 / 14:05 / 22:05. Coordinate with friends for balanced teams.",
      he: "היו מחוברים כמה דקות לפני 06:54 / 14:05 / 22:05. תאמו עם חברים לקבוצות מאוזנות.",
    },
  ],

  "other-events": [
    {
      type: "note",
      en: "Live countdowns for all timed content: /events or in-game Event Time (Asia/Jerusalem / server time).",
      he: "ספירות לאחור חיות לכל תוכן מתוזמן: /events או Event Time במשחק (Asia/Jerusalem / שעון השרת).",
    },
    {
      type: "h",
      en: "About",
      he: "אודות",
    },
    {
      type: "p",
      en: "This page summarizes other scheduled and related content. See dedicated guide topics for full details where available.",
      he: "דף זה מסכם תוכן מתוזמן וקשור נוסף. ראו נושאי מדריך ייעודיים לפרטים מלאים היכן שזמין.",
    },
    {
      type: "h",
      en: "Moss Merchant",
      he: "Moss Merchant",
    },
    {
      type: "p",
      en: "02:00 / 08:00 / 14:00 / 20:00 — NPC Moss on Elbeland when spawned. See Moss Merchant topic.",
      he: "02:00 / 08:00 / 14:00 / 20:00 — NPC Moss ב־Elbeland כשהוא מופיע. ראו נושא Moss Merchant.",
    },
    {
      type: "h",
      en: "TvT",
      he: "TvT",
    },
    {
      type: "p",
      en: "06:54 / 14:05 / 22:05, minimum level 400, reward 50 Credits. See TvT topic.",
      he: "06:54 / 14:05 / 22:05, רמה מינימלית 400, תגמול 50 Credits. ראו נושא TvT.",
    },
    {
      type: "h",
      en: "Castle Siege / Crywolf / Castle Deep",
      he: "Castle Siege / Crywolf / Castle Deep",
    },
    {
      type: "p",
      en: "All on the CS realm only. Crywolf: Wednesday & Saturday 20:30. Castle Deep: 04:00 / 12:00 / 20:00 daily. Castle Siege: check Event Time on CS for registration and siege windows. See dedicated topics.",
      he: "הכול על רילם ה־CS בלבד. Crywolf: יום רביעי ושבת 20:30. Castle Deep: 04:00 / 12:00 / 20:00 יומי. Castle Siege: בדקו Event Time ב־CS לחלונות רישום ומצור. ראו נושאים ייעודיים.",
    },
    {
      type: "h",
      en: "Double Goer",
      he: "Double Goer",
    },
    {
      type: "p",
      en: "Enabled. Entry items (14,111) / (13,125) / (14,125). NPC Lugard on Elbeland / Event Square. Schedule is not hourly — check Event Time. See Double Goer topic.",
      he: "מופעל. פריטי כניסה (14,111) / (13,125) / (14,125). NPC Lugard ב־Elbeland / Event Square. הלוח אינו שעתי — בדקו Event Time. ראו נושא Double Goer.",
    },
    {
      type: "h",
      en: "AutoEvents",
      he: "AutoEvents",
    },
    {
      type: "p",
      en: "Automated event helpers post notices during play. Watch Event Time and server messages for the next auto event.",
      he: "עוזרי אירוע אוטומטיים מפרסמים הודעות במהלך המשחק. עקבו אחרי Event Time והודעות השרת לאירוע האוטומטי הבא.",
    },
    {
      type: "h",
      en: "Battle Pass / Online Bonus",
      he: "Battle Pass / Online Bonus",
    },
    {
      type: "p",
      en: "These are systems (not timed calendar events) — see Systems → Battle Pass / Online Bonus.",
      he: "אלה מערכות (לא אירועי לוח מתוזמנים) — ראו Systems → Battle Pass / Online Bonus.",
    },
    {
      type: "h",
      en: "Premium GM events",
      he: "אירועי Premium GM",
    },
    {
      type: "p",
      en: "Optional GM-started events may appear. They are not on the fixed public schedule — watch announcements.",
      he: "אירועים אופציונליים שמופעלים על ידי GM עשויים להופיע. הם לא בלוח הציבורי הקבוע — עקבו אחרי הודעות.",
    },
  ],
};

export const eventTopicIds = Object.keys(guideEventsContent);

export default guideEventsContent;
